import semver from 'semver';

import { isEmpty } from 'utils';
import { appUpdateAvailable, isVersionGreater } from 'utils/app';
import { getCookie, setCookie } from 'utils/cookie';
import api from 'services/api';

import clientActions from './client';

const APPS_LOADING = 'apps/loading';
const APPS_FETCH = 'apps/fetch';
const APPS_FETCH_ALL = 'apps/fetchAll';
const APPS_FETCH_VERSIONED = 'apps/fetchVersioned';
const APPS_FETCH_ALL_VERSIONS = 'apps/fetchAllVersions';
const APPS_FETCH_VERSIONS_ABOVE = 'apps/fetchVersionsAbove';
const APPS_FETCH_ALL_STOREFRONT_SHOPS = 'apps/fetchAllStorefrontShops';
const APPS_FETCH_ALL_DEPRECATED = 'apps/fetchAllDeprecated';
const APPS_FETCH_CONFIGS = 'apps/fetchConfigs';
const APPS_FETCH_CONFIG_FILE = 'apps/fetchConfigFile';
const APPS_CREATE_CONFIG = 'apps/createConfig';
const APPS_FETCH_REVIEWS = 'apps/fetchReviews';
const APPS_CREATE = 'apps/create';
const APPS_UPDATE = 'apps/update';
const APPS_DELETE = 'apps/delete';
const APPS_FETCH_STOREFRONT_UPDATES = 'apps/fetchStorefrontUpdates';
const APPS_AUTO_UPDATE_INSTALLED_THEMES = 'apps/autoUpdateInstalledThemes';
const APPS_FETCH_THEME_PREVIEW_THUMBNAILS = 'apps/fetchThemePreviewThumbnails';

const actions = {
  fetchAll() {
    return {
      type: APPS_FETCH_ALL,
      payload: api.get('/apps').then((apps) => {
        return {
          ...apps,
          results: apps.results.map((app) => applyRecordProps(app)),
        };
      }),
    };
  },

  loadAll() {
    return async (dispatch, getState) => {
      const { apps } = getState();
      if (apps.fetched) {
        return Promise.resolve(apps.results);
      }
      return dispatch(this.fetchAll());
    };
  },

  fetch(id) {
    return {
      type: APPS_FETCH,
      payload: api.get(`/apps/${id}`).then((app) => applyRecordProps(app)),
    };
  },

  load(id) {
    return async (dispatch, getState) => {
      const { apps } = getState();
      const app = apps.results.find((app) => app.id === id);
      if (app) {
        await dispatch({
          type: APPS_FETCH,
          payload: app,
        });
        return Promise.resolve(app);
      }
      return dispatch(this.fetch(id));
    };
  },

  fetchWithPreviewTheme(id) {
    return async (dispatch) => {
      const app = await dispatch(this.fetch(id));
      if (app.preview_theme_id) {
        app.preview_theme = await api.get(
          `/data/:themes/${app.preview_theme_id}`,
        );
      }
      return app;
    };
  },

  fetchAllStorefrontShops(query) {
    return async (dispatch) => {
      const result = await dispatch({
        type: APPS_FETCH_ALL_STOREFRONT_SHOPS,
        payload: api.get('/apps/storefront-shops', query),
      });

      if (result) {
        await dispatch(this.fetchAllThemePreviewThumbnails(result));
      }

      return result;
    };
  },

  async fetchAllThemePreviewThumbnails(result) {
    return async (dispatch) => {
      const appsWithPreviewTheme = await Promise.all(
        result.results
          .filter(
            (app) =>
              app.type === 'theme' &&
              !app.preview_image &&
              app.preview_theme_id,
          )
          .map(async (themeApp) => {
            themeApp.preview_theme = await api.get(
              `/data/:themes/${themeApp.preview_theme_id}`,
            );
            return themeApp;
          }),
      );

      for (const app of appsWithPreviewTheme) {
        if (app.preview_theme) {
          dispatch(this.fetchThemePreviewThumbnails(app.preview_theme));
        }
      }
    };
  },

  fetchThemePreviewThumbnails(theme) {
    return (dispatch) => {
      // Only fetch the first time
      // Note: may need a way to re-trigger this in the future
      if (!theme || theme.preview_thumbnails) {
        return;
      }

      dispatch({
        type: APPS_FETCH_THEME_PREVIEW_THUMBNAILS,
        payload: {},
        meta: { id: theme.app_id, loading: true },
      });

      return dispatch({
        type: APPS_FETCH_THEME_PREVIEW_THUMBNAILS,
        payload: api.get(`/data/:themes/${theme.id}`, {
          $thumbnails: true,
        }),
        meta: { id: theme.app_id },
        showLoading: false,
      });
    };
  },

  fetchAllDeprecatedApps() {
    return {
      type: APPS_FETCH_ALL_DEPRECATED,
      payload: api.get('/apps/deprecated-apps'),
    };
  },

  fetchVersioned(id, version) {
    return {
      type: APPS_FETCH_VERSIONED,
      payload: api.get(`/apps/${id}`, { $version: version }),
    };
  },

  fetchAllVersions(id) {
    return {
      type: APPS_FETCH_ALL_VERSIONS,
      payload: api.get(`/apps/${id}/versions`),
    };
  },

  fetchVersionsAbove(id, version) {
    return {
      type: APPS_FETCH_VERSIONS_ABOVE,
      payload: api.get(`/apps/${id}/versions`, { version: { $gt: version } }),
      showError: false,
    };
  },

  fetchConfigs(id, query = undefined) {
    return {
      type: APPS_FETCH_CONFIGS,
      payload: api.get(`/apps/${id}/configs`, {
        type: { $nin: ['asset', 'file', 'frontend'] },
        ...query,
      }),
      meta: { id },
    };
  },

  fetchConfigFile(id, file_path) {
    return {
      type: APPS_FETCH_CONFIG_FILE,
      payload: api.get(`/apps/${id}/config-file-data`, {
        file_path,
      }),
      meta: { id, file_path },
    };
  },

  loadConfigFile(id, file_path) {
    return (dispatch, getState) => {
      const { apps } = getState();
      if (apps.configFiles[id]?.[file_path]) {
        return Promise.resolve(apps.configFiles[id][file_path]);
      }
      return dispatch(this.fetchConfigFile(id, file_path));
    };
  },

  fetchReviews(id) {
    return {
      type: APPS_FETCH_REVIEWS,
      payload: api.get(`/apps/${id}/reviews`),
    };
  },

  create(data) {
    return {
      type: APPS_CREATE,
      payload: api.post('/apps', data),
    };
  },

  update(id, data) {
    return {
      type: APPS_UPDATE,
      payload: api.put(`/apps/${id}`, data),
    };
  },

  delete(id, confirm_delete_key = null) {
    return {
      type: APPS_DELETE,
      payload: api.delete(`/apps/${id}`, { confirm_delete_key }),
    };
  },

  createConfig(id, data) {
    return {
      type: APPS_CREATE_CONFIG,
      payload: api.post(`/apps/${id}/configs`, data),
    };
  },

  autoUpdateInstalledThemes() {
    return (dispatch, getState) => {
      const {
        client: { apps: installedApps },
      } = getState();

      return dispatch({
        type: APPS_AUTO_UPDATE_INSTALLED_THEMES,
        payload: updateInstalledThemes(dispatch, installedApps),
      });
    };
  },

  fetchStorefrontUpdates() {
    return (dispatch, getState) => {
      const {
        storefronts,
        client: { apps: installedApps },
      } = getState();

      if (storefronts.all?.results?.length > 0) {
        return dispatch({
          type: APPS_FETCH_STOREFRONT_UPDATES,
          payload: getStorefrontCompatibleUpdates(
            dispatch,
            storefronts.all.results,
            installedApps,
          ),
        });
      }
    };
  },
};

export default actions;

export const initialState = {
  fetched: false,
  results: [],
  count: 0,
  configs: {},
  configFiles: {},
  pages: {},
  reviews: {
    results: [],
    count: 0,
    pages: {},
  },
  record: null,
  loading: false,
  storefrontShops: {
    results: [],
  },
  storefrontUpdates: {},
  deprecatedApps: {},
};

export function reducer(state = initialState, action) {
  switch (action.type) {
    case APPS_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case APPS_FETCH:
      return {
        ...state,
        record: action.payload,
      };

    case APPS_FETCH_ALL:
      return {
        ...state,
        ...action.payload,
        fetched: true,
      };

    case APPS_FETCH_CONFIGS:
      return {
        ...state,
        configs: {
          ...state.configs,
          [action.payload.app_id]: action.payload,
        },
      };

    case APPS_FETCH_CONFIG_FILE:
      return {
        ...state,
        configFiles: {
          ...state.configFiles,
          [action.meta.id]: {
            ...(state.configFiles[action.meta.id] || {}),
            [action.meta.file_path]: action.payload,
          },
        },
      };

    case APPS_FETCH_ALL_STOREFRONT_SHOPS:
      return {
        ...state,
        storefrontShops: action.payload,
      };

    case APPS_FETCH_THEME_PREVIEW_THUMBNAILS:
      const { id, loading } = action.meta;
      const { preview_thumbnails = {} } = action.payload;

      let { storefrontShops } = state;
      const record = storefrontShops.results.find((app) => app.id === id);
      if (record?.preview_theme) {
        record.preview_theme.preview_thumbnails = {
          ...(record.preview_theme.preview_thumbnails || undefined),
          ...(preview_thumbnails || undefined),
          loading,
        };
        storefrontShops = { ...storefrontShops };
      }

      return {
        ...state,
        storefrontShops,
      };

    case APPS_FETCH_ALL_DEPRECATED:
      return {
        ...state,
        deprecatedApps: action.payload,
      };

    case APPS_FETCH_REVIEWS:
      return {
        ...state,
        reviews: {
          ...state.reviews,
          ...action.payload,
        },
      };

    case APPS_FETCH_STOREFRONT_UPDATES:
      return {
        ...state,
        storefrontUpdates: action.payload,
      };

    default:
      return state;
  }
}

function applyRecordProps(app) {
  return {
    ...app,
    updateAvailable: appUpdateAvailable(app, app.installed),
  };
}

function getAppSlugId(installedApp) {
  return (
    (installedApp.app_private_id || installedApp.app?.private_id)?.replace(
      /^_/,
      '',
    ) || installedApp.app_id
  );
}

function findInstalledApp(installedApps, app_id) {
  return installedApps.results.find((installed) => installed.app_id === app_id);
}

// Helper to prevent checking this update too often
function getMinVersionsByIdCached(cacheKey) {
  return getCookie(`_swell_${cacheKey}_storefront_versions_by_id`);
}

function setMinVersionsByIdCache(cacheKey, versionsById) {
  // Expires in 1 hour
  const expires = new Date();
  expires.setHours(expires.getHours() + 1);

  setCookie(`_swell_${cacheKey}_storefront_versions_by_id`, versionsById, {
    expires,
  });
}

/**
 * Returns updated versions for each theme app by its ID.
 *
 * Dispatches `fetchVersionsAbove` for each app to retrieve versions newer than the specified one.
 *
 * @param {Function} dispatch - A function to dispatch actions
 * @param {Array<Object>} themeApps - An array of theme apps objects
 * @returns {Promise<Object<string, Array>>} An object mapping app IDs to arrays of updated versions
 */
async function getThemeAppUpdatedVersionsById(dispatch, themeApps) {
  const versionsById = {};

  await Promise.all(
    themeApps.map((themeApp) =>
      dispatch(
        actions.fetchVersionsAbove(themeApp.app_id, themeApp.version),
      ).then((versions) => {
        versionsById[themeApp.app_id] = versions;
      }),
    ),
  );

  return versionsById;
}

// Get versions above the minimum for all unique storefront and theme apps
async function getStorefrontAppVersionsById(dispatch, storefronts) {
  const minAppVersionsById = {};

  const cacheKey = `${storefronts[0]?.client_id}_${storefronts[0]?.environment_id}`;
  const minVersionsByIdCached = getMinVersionsByIdCached(cacheKey);
  if (minVersionsByIdCached) {
    // TODO: determine if we should really cache min version by a number of hours or minutes
    // return minVersionsByIdCached;
  }

  for (const storefront of storefronts || []) {
    const { app_id, app_version, theme_app_id, theme } = storefront;

    if (app_id) {
      minAppVersionsById[app_id] = minAppVersionsById[app_id] || null;
    }

    if (theme_app_id) {
      minAppVersionsById[theme_app_id] =
        minAppVersionsById[theme_app_id] || null;
    }

    if (app_version) {
      if (
        !minAppVersionsById[app_id] ||
        semver.compare(app_version, minAppVersionsById[app_id]) < 0
      ) {
        minAppVersionsById[app_id] = app_version;
      }
    }

    if (theme?.version) {
      if (
        !minAppVersionsById[theme_app_id] ||
        semver.compare(theme.version, minAppVersionsById[theme_app_id]) < 0
      ) {
        minAppVersionsById[theme_app_id] = theme.version;
      }
    }
  }

  const promises = [];
  const versionsById = {};
  for (const [appId, version] of Object.entries(minAppVersionsById)) {
    if (!version) {
      continue;
    }
    promises.push(
      dispatch(actions.fetchVersionsAbove(appId, version)).then((versions) => {
        versionsById[appId] = versions;
      }),
    );
  }

  await Promise.all(promises);
  setMinVersionsByIdCache(cacheKey, versionsById);

  return versionsById;
}

/**
 * Updates installed theme apps to their latest available versions.
 *
 * @param {Function} dispatch - A function to dispatch actions
 * @param {Object} installedApps - The object containing the list of installed apps (with a `results` array)
 */
async function updateInstalledThemes(dispatch, installedApps) {
  const themeApps = (installedApps?.results || []).filter(
    (installedApp) => installedApp?.app?.type === 'theme',
  );
  const hasThemeApps = themeApps.length > 0;

  if (!hasThemeApps) {
    return;
  }

  const versionsById = await getThemeAppUpdatedVersionsById(
    dispatch,
    themeApps,
  );
  const updates = themeApps.reduce((acc, themeApp) => {
    const maxVersion = findMaxVersion(versionsById, themeApp);

    if (maxVersion?.version) {
      acc.push(
        dispatch(
          clientActions.updateApp(themeApp.app_id, {
            version: maxVersion.version,
          }),
        ),
      );
    }

    return acc;
  }, []);

  await Promise.all(updates);
}

// Get compatible version updates indexed for all storefront apps
async function getStorefrontCompatibleUpdates(
  dispatch,
  storefronts,
  installedApps,
) {
  const versionsById = await getStorefrontAppVersionsById(
    dispatch,
    storefronts,
  );

  if (isEmpty(versionsById)) {
    return {};
  }

  const storefrontUpdates = {};

  for (const storefront of storefronts || []) {
    const { id, app_id, theme, app_version } = storefront;

    const storefrontApp = findInstalledApp(installedApps, app_id);
    const themeApp =
      theme?.app_id && findInstalledApp(installedApps, theme.app_id);

    if (!storefrontApp) {
      continue;
    }

    const updates = [];
    const versionProps = [versionsById, storefrontApp, themeApp, theme];

    if (themeApp) {
      updates.push(
        app_version && findMaxStorefrontAndThemeVersions(...versionProps),
        app_version && findMaxStorefrontVersion(...versionProps),
        findMaxThemeVersion(...versionProps),
      );
    } else if (app_id) {
      updates.push(findMaxStorefrontVersion(...versionProps));
    }

    const foundUpdates = updates.filter(Boolean);
    if (foundUpdates.length > 0) {
      storefrontUpdates[id] = foundUpdates;
    }
  }

  return storefrontUpdates;
}

function checkThemeStorefrontVersionCompatibility(
  storefrontAppSlug,
  storefrontVersion,
  themeVersion,
) {
  const { compatibilities } = themeVersion;

  // Theme compabilities[id] indicate which version of the storefront app the theme is compatible with
  const themeSupportedStorefrontAppVersion =
    compatibilities?.[storefrontAppSlug];

  if (!themeSupportedStorefrontAppVersion) {
    return true;
  } else {
    // Storefront compatibility.version indicates
    // which version of the storefront app the theme compatiility should be compared with
    const appCompatibility = storefrontVersion.properties?.theme?.compatibility;
    const appCompatibilityVersion =
      appCompatibility?.version || storefrontVersion.version;

    if (
      appCompatibilityVersion &&
      !semver.intersects(
        appCompatibilityVersion,
        themeSupportedStorefrontAppVersion,
        true,
      )
    ) {
      return false;
    }
  }

  return true;
}

function findMaxVersion(
  versionsById,
  installedApp,
  checkCompatibility = undefined,
) {
  if (!installedApp) {
    return;
  }

  let maxVersion;
  for (const versionRecord of versionsById[installedApp.app_id]?.results ||
    []) {
    if (
      isVersionGreater(versionRecord.version, installedApp.version) &&
      (!maxVersion ||
        isVersionGreater(versionRecord.version, maxVersion.version))
    ) {
      const check = checkCompatibility
        ? checkCompatibility(versionRecord)
        : true;
      if (check) {
        maxVersion = versionRecord;
      }
    }
  }

  return maxVersion;
}

function findMaxStorefrontVersion(versionsById, storefrontApp) {
  const maxVersion = findMaxVersion(versionsById, storefrontApp);
  if (maxVersion) {
    return { storefrontApp, storefrontVersion: maxVersion };
  }
}

function findMaxThemeVersion(versionsById, storefrontApp, themeApp, theme) {
  const storefrontAppSlug = getAppSlugId(storefrontApp);

  const themeAppWithStorefrontVersion = { ...themeApp, version: theme.version };

  const maxVersion = findMaxVersion(
    versionsById,
    themeAppWithStorefrontVersion,
    (themeVersion) => {
      return checkThemeStorefrontVersionCompatibility(
        storefrontAppSlug,
        themeVersion,
        storefrontApp.app,
      );
    },
  );

  if (maxVersion) {
    return {
      themeApp,
      themeVersion: maxVersion,
    };
  }
}

function findMaxStorefrontAndThemeVersions(
  versionsById,
  storefrontApp,
  themeApp,
  theme,
) {
  const storefrontAppSlug = getAppSlugId(storefrontApp);
  const themeAppWithStorefrontVersion = { ...themeApp, version: theme.version };

  let maxStorefrontVersion;

  const maxThemeVersion = findMaxVersion(
    versionsById,
    themeAppWithStorefrontVersion,
    (themeVersion) => {
      maxStorefrontVersion = findMaxVersion(
        versionsById,
        storefrontApp,
        (storefrontVersion) => {
          return checkThemeStorefrontVersionCompatibility(
            storefrontAppSlug,
            themeVersion,
            storefrontVersion,
          );
        },
      );
      if (maxStorefrontVersion) {
        return true;
      }
    },
  );

  if (maxStorefrontVersion && maxThemeVersion) {
    return {
      storefrontApp,
      themeApp,
      storefrontVersion: maxStorefrontVersion,
      themeVersion: maxThemeVersion,
    };
  }
}
